import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { apiUrl } from './api';

// Central theme tokens. Keep names generic to map existing inline styles easily
export const THEMES = {
  darkNeon: {
    key: 'darkNeon',
    label: 'Neon Dark (verde)',
    tokens: {
      isDark: true,
      bg: '#121212',
      surface: '#181818',
      panel: '#222',
      text: '#ffffff',
      textMuted: '#bfc6d1',
      border: '#2a2a2a',
      mutedBorder: '#444',
      overlayBg: 'rgba(18,18,18,0.85)',
      overlaySurface: 'linear-gradient(180deg, #1a1a1a, #151515)',
      shadow: '#000b',
      shadowSoft: '#0006',
      accent: '#00e676',
      accent2: '#009688',
      indicatorGlow: '#00e67644',
      inverse: '#0b0b0b',
    },
  },
  redLight: {
    key: 'redLight',
    label: 'Alb & roșu',
    tokens: {
      isDark: false,
      bg: '#ffffff',
      surface: '#f7f7f9',
      panel: '#ffffff',
      text: '#0b0b0b',
      textMuted: '#374151',
      border: '#e5e7eb',
      mutedBorder: '#d1d5db',
      overlayBg: 'rgba(255,255,255,0.85)',
      overlaySurface: 'linear-gradient(180deg, #ffffff, #f3f4f6)',
      shadow: '#0002',
      shadowSoft: '#0002',
      accent: '#ff3b30',
      accent2: '#c71f1a',
      indicatorGlow: '#ff3b3044',
      inverse: '#ffffff',
    },
  },
  blueLight: {
    key: 'blueLight',
    label: 'Alb & albastru',
    tokens: {
      isDark: false,
      bg: '#ffffff',
      surface: '#f5f9ff',
      panel: '#ffffff',
      text: '#0b0b0b',
      textMuted: '#1f2937',
      border: '#e0e6f0',
      mutedBorder: '#c7d2fe',
      overlayBg: 'rgba(255,255,255,0.85)',
      overlaySurface: 'linear-gradient(180deg, #ffffff, #eef4ff)',
      shadow: '#0002',
      shadowSoft: '#0002',
      accent: '#0a84ff',
      accent2: '#0066cc',
      indicatorGlow: '#0a84ff44',
      inverse: '#ffffff',
    },
  },
  purpleSlate: {
    key: 'purpleSlate',
    label: 'Violet & ardezie',
    tokens: {
      isDark: true,
      bg: '#0f1020',
      surface: '#17182b',
      panel: '#1f2038',
      text: '#eef2ff',
      textMuted: '#cbd5e1',
      border: '#2b2d4a',
      mutedBorder: '#3b3d62',
      overlayBg: 'rgba(15,16,32,0.85)',
      overlaySurface: 'linear-gradient(180deg, #1a1c36, #141629)',
      shadow: '#000b',
      shadowSoft: '#0006',
      accent: '#a78bfa',
      accent2: '#7c3aed',
      indicatorGlow: '#a78bfa44',
      inverse: '#0b0b0b',
    },
  },
  tealGray: {
    key: 'tealGray',
    label: 'Teal & gri deschis',
    tokens: {
      isDark: false,
      bg: '#f7faf9',
      surface: '#eef4f3',
      panel: '#ffffff',
      text: '#0b0b0b',
      textMuted: '#334155',
      border: '#d8e3e0',
      mutedBorder: '#cbd5d1',
      overlayBg: 'rgba(255,255,255,0.85)',
      overlaySurface: 'linear-gradient(180deg, #ffffff, #eef4f3)',
      shadow: '#0002',
      shadowSoft: '#0002',
      accent: '#14b8a6',
      accent2: '#0d9488',
      indicatorGlow: '#14b8a644',
      inverse: '#ffffff',
    },
  },
};

const ThemeContext = createContext({
  themeKey: 'darkNeon',
  tokens: THEMES.darkNeon.tokens,
  setThemeKey: () => {},
  festiveTheme: 'none',
  setFestiveTheme: () => {},
  squareTiles: false,
  setSquareTiles: () => {},
  hideFeatured: false,
  setHideFeatured: () => {},
  heroBackgroundImage: '',
  setHeroBackgroundImage: () => {}
});
export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  // Initialize immediately from localStorage (fast client render) then override with server.
  const [themeKey, setThemeKey] = useState(() => {
    try { return localStorage.getItem('themeKey') || 'darkNeon'; } catch { return 'darkNeon'; }
  });
  const [festiveTheme, setFestiveTheme] = useState(() => {
    try { return localStorage.getItem('festiveTheme') || 'none'; } catch { return 'none'; }
  });
  const [squareTiles, setSquareTiles] = useState(() => {
    try { return localStorage.getItem('squareTiles') === '1'; } catch { return false; }
  });
  const [hideFeatured, setHideFeatured] = useState(() => {
    try { return localStorage.getItem('hideFeatured') === '1'; } catch { return false; }
  });
  const [heroBackgroundImage, setHeroBackgroundImage] = useState(() => {
    try { return localStorage.getItem('heroBackgroundImage') || ''; } catch { return ''; }
  });
  const [serverLoaded, setServerLoaded] = useState(false);
  const effectiveThemeKey = themeKey || 'darkNeon';
  const tokens = useMemo(() => (THEMES[effectiveThemeKey] || THEMES.darkNeon).tokens, [effectiveThemeKey]);

  // Fetch and subscribe to settings (SSE + fallback polling)
  useEffect(() => {
    const applyData = (data) => {
      if (!data || typeof data !== 'object') return;
      console.log('[Theme] 📥 Applying server data:', JSON.stringify(data, null, 2));
      
      // ALWAYS apply server data - server is source of truth!
      if (data.themeKey !== undefined) {
        console.log('[Theme] 🎨 Setting themeKey to:', data.themeKey);
        setThemeKey(data.themeKey);
        try { localStorage.setItem('themeKey', data.themeKey); } catch {}
      }
      if (data.festiveTheme !== undefined) {
        console.log('[Theme] 🎄 Setting festiveTheme to:', data.festiveTheme);
        setFestiveTheme(data.festiveTheme);
        try { localStorage.setItem('festiveTheme', data.festiveTheme); } catch {}
      }
      if (data.squareTiles !== undefined) {
        console.log('[Theme] ⬜ Setting squareTiles to:', data.squareTiles);
        setSquareTiles(data.squareTiles);
        try { localStorage.setItem('squareTiles', data.squareTiles ? '1' : '0'); } catch {}
      }
      if (data.hideFeatured !== undefined) {
        setHideFeatured(data.hideFeatured);
        try { localStorage.setItem('hideFeatured', data.hideFeatured ? '1' : '0'); } catch {}
      }
      if (data.heroBackgroundImage !== undefined) {
        const newVal = data.heroBackgroundImage || '';
        setHeroBackgroundImage(newVal);
        try { localStorage.setItem('heroBackgroundImage', newVal); } catch {}
      }
      setServerLoaded(true);
      console.log('[Theme] ✅ State update complete!');
    };
    console.log('[Theme] 🚀 Establishing SSE connection and initial fetch...');
    console.log('[Theme] API URL:', apiUrl('/api/settings'));
    // Initial fetch - THIS IS CRITICAL FOR CROSS-BROWSER SYNC!
    fetch(apiUrl('/api/settings'))
      .then(res => {
        console.log('[Theme] ✅ Server response received, status:', res.status);
        return res.json();
      })
      .then(data => {
        console.log('[Theme] ✅ Initial settings loaded from server:', data);
        applyData(data);
      })
      .catch(err => {
        console.error('[Theme] ❌ Initial settings fetch failed:', err);
        setServerLoaded(true);
      });
    // SSE subscription
    let es;
    try {
      es = new EventSource(apiUrl('/api/settings/stream'));
      es.addEventListener('settings', (e) => {
        console.log('[Theme] 📡 SSE update received, raw data:', e.data);
        try { 
          const parsed = JSON.parse(e.data);
          console.log('[Theme] 📡 SSE parsed data:', parsed);
          applyData(parsed); 
        } catch (err) {
          console.error('[Theme] ❌ Failed to parse SSE data:', err);
        }
      });
      es.addEventListener('error', (err) => {
        console.warn('[Theme] ⚠ SSE connection error, relying on polling fallback', err);
      });
      console.log('[Theme] ✅ SSE connection established');
    } catch (e) {
      console.error('[Theme] ❌ Failed to establish settings SSE:', e);
    }
    // Fallback polling every 30s (slower since SSE is primary)
    const pollId = setInterval(() => {
      fetch(apiUrl('/api/settings'))
        .then(res => res.json())
        .then(applyData)
        .catch(() => {});
    }, 30000);
    return () => {
      console.log('[Theme] Cleaning up SSE connection');
      clearInterval(pollId);
      es && es.close();
    };
  }, []); // Empty dependency array - only run once on mount!

  useEffect(() => {
    if (!serverLoaded) return;
    try { localStorage.setItem('themeKey', themeKey); } catch {}
    try { localStorage.setItem('festiveTheme', festiveTheme); } catch {}
    try { localStorage.setItem('squareTiles', squareTiles ? '1' : '0'); } catch {}
    try { localStorage.setItem('hideFeatured', hideFeatured ? '1' : '0'); } catch {}
    try { localStorage.setItem('heroBackgroundImage', heroBackgroundImage || ''); } catch {}
  }, [themeKey, festiveTheme, squareTiles, hideFeatured, heroBackgroundImage, serverLoaded]);

  // Attach a theme class to <body> for CSS-based overrides (e.g., animated dropdown styling)
  useEffect(() => {
    console.log('[Theme] 🎨 Applying theme class to body:', themeKey);
    try {
      const clsBase = 'theme-';
      const body = document.body;
      // Remove any previous theme-* classes
      body.className = body.className
        .split(/\s+/)
        .filter(c => !c.startsWith(clsBase))
        .concat([clsBase + themeKey])
        .join(' ') // preserve other classes if any
        .trim();
      console.log('[Theme] ✅ Body class updated:', body.className);
    } catch (err) {
      console.error('[Theme] ❌ Failed to update body class:', err);
    }
  }, [effectiveThemeKey]);

  // Expose CSS custom properties for global styles (e.g., navbar hover)
  useEffect(() => {
    console.log('[Theme] 🎨 Updating CSS variables, isDark:', tokens.isDark);
    try {
      const root = document.documentElement;
      // On dark themes use subtle white; on light themes use subtle black so hover is visible
      const hover = tokens.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
      root.style.setProperty('--nav-hover-bg', hover);
      console.log('[Theme] ✅ CSS variable --nav-hover-bg set to:', hover);
    } catch (err) {
      console.error('[Theme] ❌ Failed to update CSS variables:', err);
    }
  }, [tokens]);

  const value = useMemo(() => ({
    themeKey: effectiveThemeKey,
    setThemeKey,
    tokens,
    festiveTheme,
    setFestiveTheme,
    squareTiles,
    setSquareTiles,
    hideFeatured,
    setHideFeatured,
    heroBackgroundImage,
    setHeroBackgroundImage
  }), [effectiveThemeKey, tokens, festiveTheme, squareTiles, hideFeatured, heroBackgroundImage]);
  // Delay render until we have first server snapshot to avoid reverting after user interaction
  // Always render children (no loading text). Server updates will seamlessly override.
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
